import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Box, CircularProgress } from '@mui/material';
import api from '../../services/api';
import MarkdownMessage from './MarkdownMessage';

/**
 * Component that displays text with tokenized values and resolves them dynamically
 * Tokens remain in the actual data, but are displayed as resolved values based on user permissions
 */
// eslint-disable-next-line max-lines-per-function
const TokenizedText = ({ content, entityId, variant = 'body2', sx = {} }) => {
  const [resolvedTokens, setResolvedTokens] = useState({});
  const [loading, setLoading] = useState(false);
  const [_error, setError] = useState(null);

  // Token pattern matching — must match backend format: 3-letter prefix + underscore + 12 hex chars
  // See functions/src/privacyTokenization/tokenManager.ts getTokenPrefix() for all prefixes
  const TOKEN_PATTERN = /\b([A-Z]{2,4})_([a-f0-9]{12})\b/g;

  // Extract all tokens from content
  const tokens = useMemo(() => {
    if (!content || typeof content !== 'string') return [];

    const matches = [...content.matchAll(TOKEN_PATTERN)];
    return matches.map(match => ({
      fullToken: match[0],
      type: match[1],
      id: match[2],
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  // Batch resolve tokens
  useEffect(() => {
    if (tokens.length === 0 || !entityId) return;

    const resolveTokens = async () => {
      // Check if we already have all tokens resolved
      const unresolvedTokens = tokens.filter(t => !resolvedTokens[t.fullToken]);
      if (unresolvedTokens.length === 0) return;

      setLoading(true);
      setError(null);

      try {
        // Resolve tokens individually to handle multi-word names correctly.
        // The backend replaces tokens in-place, so "PER_a PER_b" becomes
        // "John Smith Jane Doe" which can't be split back by spaces.
        const newResolvedTokens = {};

        await Promise.all(unresolvedTokens.map(async (token) => {
          try {
            const result = await api.detokenizeContent(entityId, token.fullToken);
            if (result.detokenizedContent && result.detokenizedContent !== token.fullToken) {
              newResolvedTokens[token.fullToken] = result.detokenizedContent;
            }
          } catch {
            // Individual token failed — skip it, will show as [Type] placeholder
          }
        }));

        setResolvedTokens(prev => ({ ...prev, ...newResolvedTokens }));
      } catch (err) {
        console.error('Failed to resolve tokens:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    resolveTokens();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokens, entityId]);

  // Render content with resolved tokens
  const renderContent = () => {
    if (!content || typeof content !== 'string') {
      return <Typography variant={variant} sx={sx}>{content}</Typography>;
    }

    // If no tokens found, render entire content through MarkdownMessage
    if (tokens.length === 0) {
      return <MarkdownMessage content={content} />;
    }

    // Replace tokens inline before rendering through MarkdownMessage.
    // This preserves markdown context (bold, lists, etc.) across token boundaries.
    // Resolved tokens show as bold text; unresolved tokens show as [Type] placeholder.
    const processedContent = content.replace(TOKEN_PATTERN, (match, type) => {
      const resolvedValue = resolvedTokens[match];
      if (resolvedValue) {
        return `**${resolvedValue}**`;
      }
      // Unresolved: show type label while loading or if permission denied
      return `**[${getTokenTypeLabel(type)}]**`;
    });

    return <MarkdownMessage content={processedContent} />;
  };

  // Helper functions
  const getTokenTypeLabel = (type) => {
    const labels = {
      PER: 'Person',
      ORG: 'Organization',
      LOC: 'Location',
      ADR: 'Address',
      DTE: 'Date',
      DOB: 'Date of Birth',
      EML: 'Email',
      PHN: 'Phone',
      CCN: 'Credit Card',
      SSN: 'SSN',
      RTN: 'Bank Routing',
      IBN: 'IBAN',
      SWF: 'SWIFT Code',
      EIN: 'Employer ID',
      DLN: 'Drivers License',
      PSP: 'Passport',
      MRN: 'Medical Record',
      MED: 'Medical Term',
      NPI: 'Healthcare NPI',
      DEA: 'DEA Number',
      DMG: 'Demographic',
      CRD: 'Credential',
      DIG: 'Digital Identifier',
      GID: 'Government ID',
      ATK: 'Auth Token',
      TKN: 'Token',
    };
    return labels[type] || type;
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {renderContent()}
      {loading && tokens.length > 0 && (
        <CircularProgress
          size={16}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            color: 'text.disabled'
          }}
        />
      )}
    </Box>
  );
};

export default TokenizedText;